import React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

/**
 * Avatar - User avatar with image, fallback, and status indicator
 * 
 * API podobne do Radix UI Avatar
 * - Rozmiary: "1" | "2" | "3" | "4" | "5" | "6" | "7" | "8" | "9"
 * - Variants: "solid" | "soft"
 * - Fallback: initials lub icon
 * - Status indicator: online/offline/busy etc.
 * 
 * @example
 * ```tsx
 * <Avatar src="/user.jpg" alt="Jan Kowalski" />
 * <Avatar fallback="JK" size="5" />
 * <Avatar status="online">
 *   <AvatarImage src="/user.jpg" alt="User" />
 *   <AvatarFallback>JK</AvatarFallback>
 * </Avatar>
 * ```
 */

const avatarVariants = cva(
  cn(
    "relative inline-flex items-center justify-center",
    "overflow-hidden shrink-0",
    "select-none"
  ),
  {
    variants: {
      variant: {
        solid: cn(
          "bg-[var(--dark-3)]",
          "border border-[var(--dark-a6)]"
        ),
        soft: cn(
          "bg-[var(--gray-a3)]",
          "border border-transparent"
        ),
      },
      size: {
        "1": "h-[24px] w-[24px] text-[var(--font-size-1)] rounded-[var(--radius-sm)]",
        "2": "h-[28px] w-[28px] text-[var(--font-size-1)] rounded-[var(--radius-sm)]",
        "3": "h-[32px] w-[32px] text-[var(--font-size-2)] rounded-[var(--radius-base)]",
        "4": "h-[40px] w-[40px] text-[var(--font-size-2)] rounded-[var(--radius-base)]",
        "5": "h-[48px] w-[48px] text-[var(--font-size-3)] rounded-[var(--radius-md)]",
        "6": "h-[56px] w-[56px] text-[var(--font-size-4)] rounded-[var(--radius-md)]",
        "7": "h-[64px] w-[64px] text-[var(--font-size-5)] rounded-[var(--radius-lg)]",
        "8": "h-[80px] w-[80px] text-[var(--font-size-6)] rounded-[var(--radius-lg)]",
        "9": "h-[96px] w-[96px] text-[var(--font-size-7)] rounded-[var(--radius-xl)]",
      },
      radius: {
        none: "rounded-none",
        sm: "rounded-[var(--radius-sm)]",
        base: "rounded-[var(--radius-base)]",
        md: "rounded-[var(--radius-md)]",
        lg: "rounded-[var(--radius-lg)]",
        xl: "rounded-[var(--radius-xl)]",
        full: "rounded-full",
      },
    },
    defaultVariants: {
      variant: "soft",
      size: "3",
    },
  }
)

const statusIndicatorVariants = cva(
  cn(
    "absolute bottom-0 right-0",
    "rounded-full border-2 border-[var(--dark-1)]"
  ),
  {
    variants: {
      status: {
        online: "bg-[var(--success-9)]",
        offline: "bg-[var(--gray-8)]",
        busy: "bg-[var(--danger-9)]",
        away: "bg-[var(--warning-9)]",
      },
      size: {
        "1": "h-[8px] w-[8px]",
        "2": "h-[8px] w-[8px]",
        "3": "h-[10px] w-[10px]",
        "4": "h-[10px] w-[10px]",
        "5": "h-[12px] w-[12px]",
        "6": "h-[14px] w-[14px]",
        "7": "h-[16px] w-[16px]",
        "8": "h-[18px] w-[18px]",
        "9": "h-[20px] w-[20px]",
      },
    },
    defaultVariants: {
      status: "online",
      size: "3",
    },
  }
)

export interface AvatarProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof avatarVariants> {
  /**
   * Image source
   */
  src?: string
  /**
   * Alt text for image
   */
  alt?: string
  /**
   * Fallback text (usually initials)
   */
  fallback?: string
  /**
   * Status indicator
   */
  status?: "online" | "offline" | "busy" | "away"
}

const Avatar = React.forwardRef<HTMLSpanElement, AvatarProps>(
  (
    {
      className,
      variant,
      size,
      radius,
      src,
      alt,
      fallback,
      status,
      children,
      ...props
    },
    ref
  ) => {
    const [imageError, setImageError] = React.useState(false)
    const showImage = src && !imageError

    return (
      <span
        ref={ref}
        className={cn(avatarVariants({ variant, size, radius }), className)}
        {...props}
      >
        {/* Image */}
        {showImage && (
          <img
            src={src}
            alt={alt}
            className="h-full w-full object-cover"
            onError={() => setImageError(true)}
          />
        )}

        {/* Fallback */}
        {!showImage && (
          <span
            className={cn(
              "flex h-full w-full items-center justify-center",
              "font-[var(--font-weight-medium)]",
              "text-[var(--text-white-85)] uppercase"
            )}
          >
            {fallback || children}
          </span>
        )}

        {/* Status Indicator */}
        {status && (
          <span
            className={cn(statusIndicatorVariants({ status, size }))}
            aria-label={`Status: ${status}`}
          />
        )}
      </span>
    )
  }
)
Avatar.displayName = "Avatar"

/**
 * AvatarImage - Image component for compound Avatar usage
 */
export interface AvatarImageProps
  extends React.ImgHTMLAttributes<HTMLImageElement> {}

const AvatarImage = React.forwardRef<HTMLImageElement, AvatarImageProps>(
  ({ className, ...props }, ref) => {
    return (
      <img
        ref={ref}
        className={cn("h-full w-full object-cover", className)}
        {...props}
      />
    )
  }
)
AvatarImage.displayName = "AvatarImage"

/**
 * AvatarFallback - Fallback component for compound Avatar usage
 */
export interface AvatarFallbackProps
  extends React.HTMLAttributes<HTMLSpanElement> {}

const AvatarFallback = React.forwardRef<HTMLSpanElement, AvatarFallbackProps>(
  ({ className, ...props }, ref) => {
    return (
      <span
        ref={ref}
        className={cn(
          "flex h-full w-full items-center justify-center",
          "font-[var(--font-weight-medium)]",
          "text-[var(--text-white-85)] uppercase",
          className
        )}
        {...props}
      />
    )
  }
)
AvatarFallback.displayName = "AvatarFallback"

export { Avatar, AvatarImage, AvatarFallback, avatarVariants }

